import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, MouseEvent, useEffect, useState } from "react";
import styles from "../stylesheets/components/image-slider.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  ImageSliderControlsPosition,
  ImagesModuleSettings,
  Language,
  OverlayModuleSettings,
  TranslatedModule,
} from "../types/index.js";
import { getTranslations } from "../utils/utils.js";
import Icon from "./Icon.js";
import ImageModule from "./Modules/ImageModule.js";
import { getBreakpointImageColumnsCount } from "./Modules/ImagesModule.js";
import OverlayModule from "./Modules/OverlayModule.js";
import Slider from "./Slider.js";

const portraitAspectRatio = 2 / 3;

interface Props {
  pageId: string;
  images: TranslatedModule<ImageModuleSettings>[];
  isPreview: boolean;
  isInsideHeader: boolean;
  scheme: ColorScheme;
  translatedModule: TranslatedModule<ImagesModuleSettings>;
  overlayModule: TranslatedModule<OverlayModuleSettings> | undefined;
  activeModuleId: string | undefined;
  slidesToShow: number;
  queries: ContainerQueries;
  fullHeight?: boolean;
  controlsPosition?: ImageSliderControlsPosition;
}

const translations = {
  de: { prev: "vorheriges Bild anzeigen", next: "nächstes Bild anzeigen" },
  it: {
    prev: "mostrare immagine precedente",
    next: "mostrare immagine successiva",
  },
  en: { prev: "show previous image", next: "show next image" },
  fr: {
    prev: "afficher l’image précédente",
    next: "afficher l’image suivante",
  },
};

type ArrowComponent = FunctionComponent<{
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  languageId: Language;
}>;

const PrevArrow: ArrowComponent = ({ onClick, languageId }) => {
  const i18n = getTranslations(languageId, translations);
  return (
    <button
      onClick={onClick}
      className={ClassNames(
        styles["ContentSlider__Control--prev"],
        styles.ContentSlider__Control,
      )}
      aria-label={i18n.prev}
    >
      <Icon
        className={styles.ContentSlider__Control__Icon}
        glyph="arrow-left"
      />
    </button>
  );
};

const NextArrow: ArrowComponent = ({ onClick, languageId }) => {
  const i18n = getTranslations(languageId, translations);
  return (
    <button
      onClick={onClick}
      className={ClassNames(
        styles["ContentSlider__Control--next"],
        styles.ContentSlider__Control,
      )}
      aria-label={i18n.next}
    >
      <Icon
        className={styles.ContentSlider__Control__Icon}
        glyph="arrow-right"
      />
    </button>
  );
};

const ImageSlider: FunctionComponent<Props> = ({
  images,
  isPreview,
  translatedModule: {
    settings: { mediaAspectRatio, sliderEffect, columnsCount, gap },
    translation: { languageId },
  },
  scheme,
  pageId,
  overlayModule,
  activeModuleId,
  slidesToShow: slidesToShowProp,
  isInsideHeader,
  queries,
  fullHeight,
  controlsPosition = "middle",
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const [maxSelectedIndex, setMaxSelectedIndex] = useState(0);
  const [currentIndexes, setCurrentIndexes] = useState([0, 1]);
  const slidesToShow =
    sliderEffect === "fade"
      ? 1
      : getBreakpointImageColumnsCount(queries, slidesToShowProp);

  const showArrows = images.length > slidesToShow;

  useEffect(() => {
    slider?.on("select", () => {
      const currentIndex = slider?.selectedScrollSnap() ?? 0;
      setMaxSelectedIndex((maxSelectedIndex) =>
        currentIndex > maxSelectedIndex ? currentIndex : maxSelectedIndex,
      );
      setCurrentIndexes([currentIndex - 1, currentIndex, currentIndex + 1]);
    });
  }, [slider]);

  return (
    <div
      className={ClassNames(
        styles.SliderModule,
        styles[`SliderModule--controls-${controlsPosition}`],
      )}
    >
      {showArrows && (
        <PrevArrow
          languageId={languageId}
          onClick={() => {
            if (!slider) return;
            const slidesCount = slider.slideNodes().length;
            slider.canScrollPrev()
              ? slider.scrollPrev()
              : slider.scrollTo(slidesCount);
          }}
        />
      )}
      <Slider
        className={styles.SliderModule__Slider}
        containerClassName={ClassNames(
          styles.SliderModule__Container,
          gap !== "none" && {
            [styles[`SliderModule__Container--gap-${gap}`]]:
              sliderEffect === "slide",
          },
        )}
        slideClassName={styles.SliderModule__SlideOuter}
        isPreview={isPreview}
        onSliderLoaded={setSlider}
        slidesToShow={slidesToShow}
        sliderEffect={sliderEffect}
        activePreviewSlideId={activeModuleId}
        slideIds={images.map(({ id }) => id)}
      >
        {images.map((image, index) => (
          <div className={styles.SliderModule__Slide} key={image.id}>
            {(index <= maxSelectedIndex + columnsCount ||
              sliderEffect === "slide") && (
              <ImageModule
                key={image.id}
                aspectRatio={mediaAspectRatio}
                portraitAspectRatio={
                  fullHeight && slidesToShow === 1
                    ? portraitAspectRatio
                    : undefined
                }
                translatedModule={image}
                width={1920}
                lazyLoad={
                  !(
                    (index === 0 && isInsideHeader) ||
                    currentIndexes.includes(index)
                  )
                }
                sizes="100vw"
                isPreview={isPreview}
                isSlider={true}
                pageId={pageId}
                scheme={scheme}
                showOverlay={!overlayModule}
              />
            )}
          </div>
        ))}
      </Slider>
      {showArrows && (
        <NextArrow
          languageId={languageId}
          onClick={() => {
            slider?.canScrollNext()
              ? slider?.scrollNext()
              : slider?.scrollTo(0);
          }}
        />
      )}

      {overlayModule && (
        <OverlayModule
          isPreview={isPreview}
          scheme={scheme}
          isSlider={true}
          translatedModule={overlayModule}
          pageId={pageId}
        />
      )}
    </div>
  );
};

export default ImageSlider;
